import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'

const FAQ: { q: string; a: string }[] = [
  {
    q: 'How do I check an SSL certificate expiration date from the command line?',
    a: 'Run: echo | openssl s_client -servername example.com -connect example.com:443 2>/dev/null | openssl x509 -noout -enddate. The notAfter line is the expiry date. Add -checkend 1209600 instead of -enddate to get a non-zero exit code when fewer than 14 days are left.',
  },
  {
    q: 'How many days before expiry should I be warned?',
    a: 'At least 14 days, so a failed renewal has time to be fixed during working hours. Teams that renew by hand or through a ticket queue often use 30 days. Uptrack warns 14 days ahead by default; set cert_warning_days on the monitor to change it.',
  },
  {
    q: 'My site works in the browser. Why does a check report a certificate chain error?',
    a: 'Usually the server does not send its intermediate certificate. Desktop browsers can fetch the missing intermediate on their own, but curl, API clients, payment webhooks and many mobile apps cannot. The fix is to serve the full chain (for example fullchain.pem instead of cert.pem).',
  },
  {
    q: 'Do auto-renewing certificates like Let’s Encrypt still need monitoring?',
    a: 'Yes. Monitoring checks the certificate your servers actually serve, so it catches a renewal job that stopped, a renewed certificate that was never reloaded, and an endpoint that still serves the old one. Let’s Encrypt stopped sending expiry reminder emails in 2025, so nothing else warns you.',
  },
  {
    q: 'Can Uptrack monitor certificates on internal hosts?',
    a: 'No. Checks run from public probes and only connect to public addresses, so hosts on a private network or behind a VPN cannot be checked.',
  },
]

export const Route = createFileRoute('/_site/guides/ssl-monitoring')({
  component: SslMonitoringGuidePage,
  head: () => ({
    meta: [
      { title: 'Check SSL Certificate Expiration (and Get Warned) | Uptrack' },
      {
        name: 'description',
        content:
          'Check an SSL certificate expiration date with openssl, curl, Python or Node, see why renewals fail silently, and get warned before a certificate expires.',
      },
      { property: 'og:title', content: 'How to check SSL certificate expiration, and get warned in time' },
      {
        property: 'og:description',
        content:
          'Commands to check certificate expiry, the renewal failures they miss, and how to monitor every endpoint you serve.',
      },
      { property: 'og:image', content: 'https://uptrack.app/og/tools-ssl-checker.png' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/guides/ssl-monitoring' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'How to check SSL certificate expiration, and get warned before it expires',
          description:
            'Check an SSL certificate expiration date from the command line or code, and monitor certificates so a failed renewal is caught in time.',
          datePublished: '2026-10-10',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack' },
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        }),
      },
    ],
  }),
})

function Code({ children }: { children: string }) {
  return (
    <div className="mb-6 rounded-lg border border-border bg-muted p-4">
      <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
        <code className="text-muted-foreground">{children}</code>
      </pre>
    </div>
  )
}

const P = 'mb-4 text-lg leading-[1.7] text-body'
const H2 = 'font-sans mb-4 text-2xl font-semibold'
const H3 = 'font-sans mb-2 mt-6 text-lg font-semibold'

const COMPARISON: { option: string; cells: [string, string, string, string] }[] = [
  { option: 'Calendar reminder', cells: ['No', 'No', 'No', 'One person'] },
  { option: 'CA expiry emails', cells: ['Some CAs', 'No', 'No', 'One inbox'] },
  { option: 'Cron job + openssl', cells: ['Yes', 'Yes, if you list them', 'Partly', 'You build it'] },
  { option: 'Uptrack SSL monitor', cells: ['Yes', 'Yes, one monitor each', 'Yes', 'Email, Slack, Discord, webhooks'] },
]

function SslMonitoringGuidePage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="font-sans mb-3 text-sm font-medium uppercase tracking-wide text-primary">Monitoring</p>
      <h1 className="font-sans mb-4 text-4xl font-semibold">
        How to check an SSL certificate’s expiration date, and get warned in time
      </h1>
      <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
        Checking one certificate takes one command. Knowing that every certificate you serve will still be valid
        next month is harder. This guide covers both: the commands, the failures they miss, and how to monitor
        certificates so a failed renewal reaches you before it reaches your users.
      </p>

      <section className="mb-10">
        <h2 className={H2}>Check it now</h2>
        <p className={P}>
          The quickest way is the{' '}
          <Link to="/tools/ssl-checker" className="font-medium text-primary hover:underline">
            SSL checker
          </Link>
          : enter a hostname and see the expiry date, the issuer, the names the certificate covers and whether the
          chain is valid. To do it yourself, use one of these.
        </p>

        <h3 className={H3}>openssl</h3>
        <Code>{`# Print the expiry date (notAfter)
echo | openssl s_client -servername example.com -connect example.com:443 2>/dev/null \\
  | openssl x509 -noout -enddate
# notAfter=Jan 14 23:59:59 2027 GMT

# Exit code 1 if the certificate expires within 14 days (1209600 seconds)
echo | openssl s_client -servername example.com -connect example.com:443 2>/dev/null \\
  | openssl x509 -noout -checkend 1209600`}</Code>
        <p className={P}>
          Keep <code>-servername</code>. Without it, a server that hosts several sites can send a different
          certificate than the one your visitors get.
        </p>

        <h3 className={H3}>curl</h3>
        <Code>{`curl -sv https://example.com -o /dev/null 2>&1 | grep -i "expire date"
# *  expire date: Jan 14 23:59:59 2027 GMT`}</Code>

        <h3 className={H3}>Python</h3>
        <Code>{`import socket, ssl
from datetime import datetime, timezone

def days_left(host: str, port: int = 443) -> int:
    ctx = ssl.create_default_context()  # verifies the chain and the hostname
    with socket.create_connection((host, port), timeout=10) as sock:
        with ctx.wrap_socket(sock, server_hostname=host) as tls:
            not_after = tls.getpeercert()["notAfter"]
    expires = datetime.fromtimestamp(ssl.cert_time_to_seconds(not_after), timezone.utc)
    return (expires - datetime.now(timezone.utc)).days

print(days_left("example.com"))`}</Code>

        <h3 className={H3}>Node.js</h3>
        <Code>{`import tls from 'node:tls'

const socket = tls.connect({ host: 'example.com', port: 443, servername: 'example.com' }, () => {
  const { valid_to } = socket.getPeerCertificate()
  const days = Math.floor((new Date(valid_to) - Date.now()) / 86_400_000)
  console.log(valid_to, days, 'days left', socket.authorized ? '' : socket.authorizationError)
  socket.end()
})`}</Code>
      </section>

      <section className="mb-10">
        <h2 className={H2}>Why certificates still expire on teams that automate renewal</h2>
        <p className={P}>
          Most expired certificates were set to renew automatically. The renewal broke, and nobody noticed. The
          usual causes:
        </p>
        <ul className="mb-4 space-y-3">
          <li className="text-lg leading-[1.7] text-body">
            <strong className="text-foreground">The renewal job stopped.</strong> A server rebuild dropped the
            timer, a DNS API token used for validation was rotated, or the job hit the CA’s rate limit. It fails
            quietly in a log nobody reads.
          </li>
          <li className="text-lg leading-[1.7] text-body">
            <strong className="text-foreground">The new certificate was never loaded.</strong> The file on disk is
            fresh, but the web server or load balancer still serves the old one from memory.
          </li>
          <li className="text-lg leading-[1.7] text-body">
            <strong className="text-foreground">One endpoint was missed.</strong> The main site renewed; the API
            subdomain, the CDN origin or a second region still has the old certificate.
          </li>
          <li className="text-lg leading-[1.7] text-body">
            <strong className="text-foreground">The chain is incomplete.</strong> Browsers fill in a missing
            intermediate certificate, so the site looks fine. curl, payment webhooks and mobile apps refuse the
            connection.
          </li>
          <li className="text-lg leading-[1.7] text-body">
            <strong className="text-foreground">Nobody sends reminders any more.</strong> Let’s Encrypt ended its
            expiry emails in 2025, and maximum certificate lifetimes are getting shorter (200 days since March
            2026, falling to 47 days by 2029). Renewals happen more often, so there are more chances for one to
            fail.
          </li>
        </ul>
        <p className={P}>
          A one-off check catches none of this next month. You need the same check, on a schedule, against every
          hostname you serve, with an alert that reaches someone.
        </p>
      </section>

      <section className="mb-10">
        <h2 className={H2}>The do-it-yourself version</h2>
        <p className={P}>
          A cron job with openssl works if you keep the host list up to date and make sure its output reaches a
          person:
        </p>
        <Code>{`#!/usr/bin/env bash
# /etc/cron.daily/cert-check : mail a warning 21 days before any listed certificate expires
for host in example.com api.example.com shop.example.com; do
  if ! echo | openssl s_client -servername "$host" -connect "$host:443" 2>/dev/null \\
       | openssl x509 -noout -checkend $((21*86400)) >/dev/null; then
    echo "$host: certificate expires within 21 days or could not be checked" \\
      | mail -s "TLS warning: $host" ops@example.com
  fi
done`}</Code>
        <p className={P}>
          The weak points: it runs on one server (if that server dies, the warnings stop), it depends on local mail
          delivery, and <code>openssl x509</code> does not verify the chain, so an incomplete chain passes.
        </p>
      </section>

      <section className="mb-10">
        <h2 className={H2}>Monitoring certificates with Uptrack</h2>
        <p className={P}>
          An SSL monitor opens a new TLS connection on every check and verifies the full chain and the hostname,
          the same way a strict client does. The monitor goes down, and you get an alert, when:
        </p>
        <ul className="mb-4 space-y-3">
          <li className="text-lg leading-[1.7] text-body">the handshake fails (broken chain, wrong hostname, untrusted issuer),</li>
          <li className="text-lg leading-[1.7] text-body">the certificate has expired, or</li>
          <li className="text-lg leading-[1.7] text-body">
            it expires within the warning window: 14 days by default, set with <code>cert_warning_days</code>.
          </li>
        </ul>
        <p className={P}>
          Add one monitor per hostname you serve. In the dashboard, choose the SSL type. With the{' '}
          <a href="/docs" className="font-medium text-primary hover:underline">
            REST API
          </a>
          :
        </p>
        <Code>{`curl -X POST https://api.uptrack.app/api/monitors \\
  -H "Authorization: Bearer $UPTRACK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "api.example.com certificate",
    "url": "https://api.example.com",
    "monitor_type": "ssl",
    "interval": 3600,
    "settings": { "cert_warning_days": 30 }
  }'`}</Code>
        <p className={P}>
          An hourly check is plenty for expiry. HTTPS uptime monitors also fail on certificate errors, so a broken
          chain on a page you already watch shows up within minutes. If you use Claude, the{' '}
          <Link to="/guides/mcp" className="font-medium text-primary hover:underline">
            Uptrack MCP server
          </Link>{' '}
          can create these monitors for you: “add an SSL monitor for each of my API hostnames”.
        </p>
      </section>

      <section className="mb-10">
        <h2 className={H2}>Options compared</h2>
        <div className="mb-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Option</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Catches a failed renewal</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Every endpoint</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Broken chain</th>
                <th className="pb-3 text-left font-medium text-muted-foreground">Who hears about it</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map(({ option, cells }) => (
                <tr key={option} className="border-b border-border">
                  <td className="py-3 pr-4 font-medium text-foreground">{option}</td>
                  {cells.map((cell, i) => (
                    <td key={i} className="py-3 pr-4 text-body">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={P}>
          Other monitoring services offer SSL checks too, some on free plans. Compare how often they check, whether
          they verify the chain, and where their alerts can go.
        </p>
      </section>

      <section className="mb-10">
        <h2 className={H2}>When you do not need this</h2>
        <ul className="space-y-3">
          <li className="text-lg leading-[1.7] text-body">
            Your only certificates are issued and served by a platform such as a managed host or CDN that renews
            them and owns the outcome.
          </li>
          <li className="text-lg leading-[1.7] text-body">
            Your certificates are on a private network. Uptrack checks run from public probes and do not connect to
            private addresses.
          </li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className={H2}>FAQ</h2>
        {FAQ.map(({ q, a }) => (
          <div key={q} className="mb-6">
            <h3 className="font-sans mb-2 text-lg font-semibold">{q}</h3>
            <p className="text-lg leading-[1.7] text-body">{a}</p>
          </div>
        ))}
      </section>

      <div className="font-sans mt-12 rounded-lg border border-border bg-muted p-6 text-center">
        <h2 className="mb-2 text-2xl font-semibold">Hear about a failed renewal weeks before it expires</h2>
        <p className="mx-auto mb-6 max-w-lg text-base text-body">
          SSL monitors are included in every plan, from $3/mo, with a 30-day money-back guarantee.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center rounded-lg bg-cta px-6 py-3 font-medium text-cta-foreground hover:opacity-90"
        >
          See plans <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
        <p className="mt-4 text-sm text-muted-foreground">
          More detail on the feature:{' '}
          <Link to="/features/ssl-monitoring" className="font-medium text-primary hover:underline">
            SSL certificate monitoring
          </Link>
          .
        </p>
      </div>
    </article>
  )
}
